"use client";
import { UpdateBlocks, UpdateBlocksPage } from "@/lib/api";
import {
    IAccountAssets,
    IBlacklistUrls,
    IBlocks,
    IBlog,
    IBlogPost,
    IConnections,
    IPages,
    ITheme,
    IUserData,
    IUsersBrandAccounts,
} from "@/types";
import React from "react";
import { ReactSortable } from "react-sortablejs";
import { toast } from "sonner";
import BlockLink from "./link";
import BlockEmail from "./email";
import BlockDivider from "./divider";
import BlockTwitch from "./twitch";
import BlockYoutube from "./youtube";
import BlockYoutubeVideo from "./youtubeVideo";
import BlockYoutubeVideoLatest from "./youtubeVideoLatest";
import BlockGithubUser from "./githubUser";
import BlockSpotify from "./spotify";
import BlockImage from "./image";
import { getTextColorForBackground } from "@/lib/utils";
import BlockPodbean from "./podbean";
import BlockMediaKit from "./mediakit";
import BlockText from "./textblock";
import BlockRichText from "./richTextBlock";
import BlockSocialButtons from "./socialButtons";
import BlockCarousel from "./carousel";
import BlockCopyLink from "./copyLink";
import BlockVrcdnLive from "./vrcdnLive";
import BlockBlogPostSingle from "./blogPostSingle";
import BlockBlogPostLatest from "./blogPostLatest";
import BlockBlogPostAll from "./blogPostAll";
import BlockTwitterFeed from "./twitterFeed";
import AddBlock from "../../content/AddBlock";
import BlockFourthWallProducts from "./fourthwall";
import BlockForm from "./form";
import BlockThroneWishlist from "./throneWishlist";
import BlockKick from "./kick";

export type BlockUpdateFunction = (
    id: string,
    updatedBlock: IBlocks,
    remove?: boolean
) => void;

interface Props {
    user: IUserData;
    admin?: boolean;
    blocks: IBlocks[];
    assets: IAccountAssets[];
    page?: IPages;
    theme: ITheme;
    selectedPreviewDevice?: string;
    connections: {
        id: string;
        provider:
        | "twitch"
        | "youtube"
        | "twitter"
        | "bluesky"
        | "threads"
        | "instagram"
        | "tiktok"
        | "trovo";
        data: {
            [key: string]: any;
        };
    }[];
    impersonate?: boolean;
    ws?: WebSocket | null;
    loggedInUser?: IUsersBrandAccounts | undefined;
    posts?: IBlogPost[];
    blogTheme?: IBlog["theme"];
    blackListUrls?: IBlacklistUrls[] | null;
}
let reorderTimeout: any = "";
const arraysAreEqual = (arr1: any[], arr2: any[]) => {
    return JSON.stringify(arr1) === JSON.stringify(arr2);
};
export default function Blocks({
    user,
    admin,
    blocks,
    assets,
    page,
    theme,
    selectedPreviewDevice,
    connections,
    impersonate,
    ws,
    loggedInUser,
    posts,
    blogTheme,
    blackListUrls,
}: Props) {
    const BlockClickHandler = (button: React.MouseEvent, block: IBlocks) => {
        const contentWarning = blackListUrls?.find((e) =>
            block.url?.includes(e.find)
        );
        if ((button.button == 0 && !admin) || (button.button == 1 && !admin)) {
            if (
                block.type == "divider" ||
                block.type == "blog_post_all" ||
                block.type === "carousel" ||
                block.type === "vrcdn_live" ||
                block.type === "rich_text_block" ||
                block.type === "social_buttons" ||
                block.type === "form" ||
                contentWarning ||
                block.adult
            )
                return;
            fetch("/api/user/click", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    account_id: user.id,
                    block_id: block.id,
                }),
            });
        }
    };
    const [newBlock, setNewBlock] = React.useState<string | null>(null);
    const [Blocks, setBlocks] = React.useState<IBlocks[]>(
        admin ? blocks : blocks.filter((e) => e.show)
    );
    const [oldBlocks, setOldBlocks] = React.useState<IBlocks[]>(blocks);
    React.useEffect(() => {
        if (admin) {
            setBlocks(oldBlocks);
        } else {
            setBlocks(oldBlocks.filter((e) => e.show));
        }
    }, [admin]);
    React.useEffect(() => {
        const handleBlocksUpdate = (
            event: CustomEvent<{ data: IBlocks[] }>
        ) => {
            if (event.detail.data) {
                let new_blocks: IBlocks[] = event.detail.data;
                setBlocks(new_blocks);
                // setBlocks([]);
                // setTimeout(() => {
                //     setBlocks(new_blocks);
                // }, 10);
            }
        };
        //@ts-expect-error
        window.addEventListener("ws_update_blocks", handleBlocksUpdate);

        return () => {
            //@ts-expect-error
            window.removeEventListener("ws_update_blocks", handleBlocksUpdate);
        };
    }, []); // Dependency array is empty because the effect does not depend on any props or state

    const reorderOrder = async (blocks_blocks: IBlocks[]) => {
        // Function to execute after debounce time
        const executeReorder = async () => {
            if (arraysAreEqual(oldBlocks, blocks_blocks)) return;
            console.log("reorder");
            if (page) {
                if (impersonate && ws && loggedInUser?.role !== "user") {
                    ws.send(
                        JSON.stringify({
                            type: "update_blocks",
                            blocks: blocks_blocks,
                        })
                    );
                }
                toast.promise(
                    UpdateBlocksPage(user, blocks_blocks, page, impersonate),
                    {
                        loading: "Updating...",
                        id: "update_blocks",
                        success: (data) => {
                            return "Changes updated!";
                        },
                        error: (error) => {
                            return `Failed to update blocks: ${error}`;
                        },
                    }
                );
                setOldBlocks(blocks_blocks);
                // Clear the timeout reference
                reorderTimeout = null;
                return;
            }
            if (impersonate && ws && loggedInUser?.role !== "user") {
                ws.send(
                    JSON.stringify({
                        type: "update_blocks",
                        blocks: blocks_blocks,
                    })
                );
            }
            toast.promise(UpdateBlocks(user, blocks_blocks, impersonate), {
                loading: "Updating...",
                id: "update_blocks",
                success: (data) => {
                    return "Changes updated!";
                },
                error: (error) => {
                    return `Failed to update blocks: ${error}`;
                },
            });
            setOldBlocks(blocks_blocks);
            // Clear the timeout reference
            reorderTimeout = null;
        };
        // Clear the previous timeout if it exists
        if (reorderTimeout) {
            clearTimeout(reorderTimeout);
        }
        // Set a new timeout
        reorderTimeout = setTimeout(executeReorder, 1000);
    };
    // Function to debounce the reorderOrder function
    const debouncedReorderOrder = (() => {
        let debounceTimeout: NodeJS.Timeout | null = null;
        return async (items: IBlocks[]) => {
            // setBlocks(items);
            // Clear previous debounce timeout
            if (debounceTimeout) {
                clearTimeout(debounceTimeout);
            }
            // Set a new debounce timeout
            debounceTimeout = setTimeout(() => {
                reorderOrder(items);
            }, 1000);
        };
    })();
    const setListHandler = (newState: IBlocks[]) => {
        if (admin) {
            setBlocks(newState);
            debouncedReorderOrder(newState);
        }
    };
    React.useEffect(() => {
        const abortController = new AbortController();
        window.addEventListener(
            "mobile_block_add",
            (event: any) => {
                const block: IBlocks = event.detail.block;
                const newBlocks = [...Blocks, block];
                setBlocks(newBlocks);
                debouncedReorderOrder(newBlocks);
                setNewBlock(block.id);
            },
            {
                signal: abortController.signal,
            }
        );
        return () => {
            abortController.abort();
        };
    });
    React.useEffect(() => {
        const abortController = new AbortController();
        window.addEventListener(
            "block_added",
            (event: any) => {
                const id: IBlocks["id"] = event.detail.id;
                setNewBlock(id);
            },
            {
                signal: abortController.signal,
            }
        );
        return () => {
            abortController.abort();
        };
    });
    const BlockUpdate: BlockUpdateFunction = (id, updatedBlock, remove) => {
        if (remove) {
            if (page) {
                fetch(
                    process.env.NEXT_PUBLIC_API_SERVER_INTERNAL +
                    "/api/admin/pages/blocks/remove",
                    {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({
                            block_id: id,
                            impersonate: impersonate ? user.id : null,
                        }),
                    }
                );
                const updatedItems = Blocks.filter((item) => item.id !== id);
                if (impersonate && ws && loggedInUser?.role !== "user") {
                    ws.send(
                        JSON.stringify({
                            type: "update_blocks",
                            blocks: updatedItems,
                        })
                    );
                }
                setBlocks(updatedItems);
                toast.promise(
                    UpdateBlocksPage(user, updatedItems, page, impersonate),
                    {
                        loading: "Updating...",
                        id: "update_blocks",
                        success: (data) => {
                            return "Changes updated!";
                        },
                        error: (error) => {
                            return `Failed to update blocks: ${error}`;
                        },
                    }
                );
                setOldBlocks(updatedItems);
                return;
            }
            fetch(
                process.env.NEXT_PUBLIC_API_SERVER_INTERNAL +
                "/api/admin/blocks/remove",
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        block_id: id,
                        impersonate: impersonate ? user.id : null,
                    }),
                }
            );
            const updatedItems = Blocks.filter((item) => item.id !== id);
            setBlocks(updatedItems);
            if (impersonate && ws && loggedInUser?.role !== "user") {
                ws.send(
                    JSON.stringify({
                        type: "update_blocks",
                        blocks: updatedItems,
                    })
                );
            }
            toast.promise(UpdateBlocks(user, updatedItems, impersonate), {
                loading: "Updating...",
                id: "update_blocks",
                success: (data) => {
                    return "Changes updated!";
                },
                error: (error) => {
                    return `Failed to update blocks: ${error}`;
                },
            });
            setOldBlocks(updatedItems);
            return;
        }
        const updatedItems = Blocks.map((item) => {
            if (item.id === id) {
                return updatedBlock; // Replace the entire item
            }
            return item;
        });
        if (page) {
            setBlocks(updatedItems);
            if (impersonate && ws && loggedInUser?.role !== "user") {
                ws.send(
                    JSON.stringify({
                        type: "update_blocks",
                        blocks: updatedItems,
                    })
                );
            }
            toast.promise(
                UpdateBlocksPage(user, updatedItems, page, impersonate),
                {
                    loading: "Updating...",
                    id: "update_blocks",
                    success: (data) => {
                        return "Changes updated!";
                    },
                    error: (error) => {
                        return `Failed to update blocks: ${error}`;
                    },
                }
            );
            setOldBlocks(updatedItems);
            return;
        }
        setBlocks(updatedItems);
        if (impersonate && ws && loggedInUser?.role !== "user") {
            ws.send(
                JSON.stringify({
                    type: "update_blocks",
                    blocks: updatedItems,
                })
            );
        }
        toast.promise(UpdateBlocks(user, updatedItems, impersonate), {
            loading: "Updating...",
            id: "update_blocks",
            success: (data) => {
                return "Changes updated!";
            },
            error: (error) => {
                return `Failed to update blocks: ${error}`;
            },
        });
        setOldBlocks(updatedItems);
    };
    return (
        <div
            className={`flex w-full flex-col items-center gap-1 ${admin && "rounded-md border-2 border-dashed border-muted-foreground p-1"}`}
        >
            {admin && <AddBlock brand={impersonate} account={user} inBlocks />}

            <ReactSortable
                id={"items"}
                list={Blocks}
                setList={setListHandler}
                disabled={!admin || loggedInUser?.role == "user"}
                handle=".handle"
                animation={500}
                className="relative flex min-h-10 w-full flex-col gap-4"
                scroll={true}
                bubbleScroll={true}
                scrollSensitivity={100}
                scrollSpeed={20}
                forceAutoScrollFallback={true}
                group={{ name: "user-blocks", pull: false, put: true }}
                delayOnTouchOnly={true}
                delay={500}
                onStart={() => {
                    if (typeof window === "undefined") return;
                    window.dispatchEvent(
                        new CustomEvent("closeBlock", {
                            detail: {
                                id: "-1",
                            },
                        })
                    );
                    window.dispatchEvent(new CustomEvent("collapseBlock"));
                }}
            // onAdd={(e) => {
            //     setNewBlock(e.item.dataset.id || null);
            // }}
            >
                {Blocks.map((block, index) => {
                    let style: any = {
                        borderWidth: "2px",
                        borderColor: "transparent",
                        background: theme.button,
                        color: theme.text_col_button
                            ? theme.text_col_button
                            : getTextColorForBackground(theme.button),
                    };
                    if (theme.inset) {
                        style = {
                            backgroundColor: "#00000000",
                            borderWidth: "2px",
                            borderColor: theme.button,
                            color: theme.text_col_button
                                ? theme.text_col_button
                                : getTextColorForBackground(theme.background),
                        };
                    }
                    if (block.colour?.toggle) {
                        style = {
                            borderWidth: "2px",
                            borderColor: "transparent",
                            background: block.colour.background,
                            color: block.colour.text
                                ? block.colour.text
                                : getTextColorForBackground(
                                    block.colour.background
                                ),
                        };
                        if (theme.inset) {
                            style = {
                                backgroundColor: "#00000000",
                                borderWidth: "2px",
                                borderColor: block.colour.background,
                                color: block.colour.text
                                    ? block.colour.text
                                    : getTextColorForBackground(
                                        block.colour.background
                                    ),
                            };
                        }
                    }
                    return (
                        <div
                            key={block.id}
                            className={`handle hover:bg-component-accent bg-component group relative flex w-full items-center rounded-md transition-colors block-${block.id}`}
                            onClick={(e) => {
                                BlockClickHandler(e, block);
                            }}
                            onAuxClick={(e) => {
                                BlockClickHandler(e, block);
                            }}
                        >
                            {/* {admin && (
                                <div className="handle absolute -left-9 top-0 z-40 flex cursor-grab gap-3 rounded-md bg-card p-1 transition-all duration-300 hover:bg-primary group-hover:opacity-100">
                                    <IconGripVertical />
                                </div>
                            )} */}
                            {RenderBlocks(block, style)}
                        </div>
                    );
                })}
            </ReactSortable>
        </div>
    );
    function RenderBlocks(block: IBlocks, style: any) {
        return (() => {
            switch (block.type) {
                case "link":
                    return (
                        <BlockLink
                            key={block.id}
                            user={user}
                            block={block}
                            update={BlockUpdate}
                            admin={admin}
                            style={style}
                            assets={assets}
                            theme={theme}
                            newBlock={newBlock}
                            blackListUrls={blackListUrls}
                        />
                    );
                case "copy_link":
                    return (
                        <BlockCopyLink
                            key={block.id}
                            user={user}
                            block={block}
                            update={BlockUpdate}
                            admin={admin}
                            style={style}
                            assets={assets}
                            theme={theme}
                            newBlock={newBlock}
                        />
                    );
                case "email":
                    return (
                        <BlockEmail
                            key={block.id}
                            user={user}
                            block={block}
                            update={BlockUpdate}
                            admin={admin}
                            style={style}
                            assets={assets}
                            theme={theme}
                            newBlock={newBlock}
                        />
                    );
                case "divider":
                    return (
                        <BlockDivider
                            key={block.id}
                            user={user}
                            block={block}
                            update={BlockUpdate}
                            admin={admin}
                            newBlock={newBlock}
                        />
                    );
                case "textblock":
                    return (
                        <BlockText
                            key={block.id}
                            user={user}
                            block={block}
                            update={BlockUpdate}
                            admin={admin}
                            style={style}
                            theme={theme}
                            newBlock={newBlock}
                        />
                    );
                case "twitch":
                    return (
                        <BlockTwitch
                            key={block.id}
                            user={user}
                            block={block}
                            update={BlockUpdate}
                            admin={admin}
                            style={style}
                            theme={theme}
                            newBlock={newBlock}
                        />
                    );
                case "vrcdn_live":
                    return (
                        <BlockVrcdnLive
                            key={block.id}
                            user={user}
                            block={block}
                            update={BlockUpdate}
                            admin={admin}
                            style={style}
                            theme={theme}
                            newBlock={newBlock}
                        />
                    );
                case "youtube":
                    return (
                        <BlockYoutube
                            key={block.id}
                            user={user}
                            block={block}
                            update={BlockUpdate}
                            admin={admin}
                            style={style}
                            theme={theme}
                            newBlock={newBlock}
                        />
                    );
                case "youtube_video":
                    return (
                        <BlockYoutubeVideo
                            key={block.id}
                            user={user}
                            block={block}
                            update={BlockUpdate}
                            admin={admin}
                            theme={theme}
                            newBlock={newBlock}
                        />
                    );
                case "youtube_video_latest":
                    return (
                        <BlockYoutubeVideoLatest
                            key={block.id}
                            user={user}
                            block={block}
                            update={BlockUpdate}
                            admin={admin}
                            theme={theme}
                            newBlock={newBlock}
                        />
                    );
                case "github_user":
                    return (
                        <BlockGithubUser
                            key={block.id}
                            user={user}
                            block={block}
                            update={BlockUpdate}
                            admin={admin}
                            newBlock={newBlock}
                        />
                    );
                case "spotify":
                    return (
                        <BlockSpotify
                            key={block.id}
                            user={user}
                            block={block}
                            update={BlockUpdate}
                            admin={admin}
                            newBlock={newBlock}
                        />
                    );
                case "img":
                    return (
                        <BlockImage
                            key={block.id}
                            user={user}
                            block={block}
                            update={BlockUpdate}
                            admin={admin}
                            assets={assets}
                            newBlock={newBlock}
                            impersonate={impersonate ? user : null}
                        />
                    );
                case "podbean":
                    return (
                        <BlockPodbean
                            key={block.id}
                            user={user}
                            block={block}
                            update={BlockUpdate}
                            admin={admin}
                            style={style}
                            theme={theme}
                            selectedPreviewDevice={selectedPreviewDevice}
                            newBlock={newBlock}
                        />
                    );
                case "mediakit":
                    return (
                        <BlockMediaKit
                            key={block.id}
                            user={user}
                            block={block}
                            update={BlockUpdate}
                            admin={admin}
                            style={style}
                            theme={theme}
                            selectedPreviewDevice={selectedPreviewDevice}
                            connections={connections}
                            newBlock={newBlock}
                        />
                    );
                case "rich_text_block":
                    return (
                        <BlockRichText
                            key={block.id}
                            user={user}
                            block={block}
                            update={BlockUpdate}
                            admin={admin}
                            style={style}
                            theme={theme}
                            newBlock={newBlock}
                            assets={assets}
                            impersonate={impersonate ? user : null}
                        />
                    );
                case "social_buttons":
                    return (
                        <BlockSocialButtons
                            key={block.id}
                            user={user}
                            block={block}
                            update={BlockUpdate}
                            admin={admin}
                            style={style}
                            theme={theme}
                            assets={assets}
                            newBlock={newBlock}
                            blackListUrls={blackListUrls}
                        />
                    );
                case "carousel":
                    return (
                        <BlockCarousel
                            key={block.id}
                            user={user}
                            block={block}
                            update={BlockUpdate}
                            admin={admin}
                            style={style}
                            theme={theme}
                            assets={assets}
                            newBlock={newBlock}
                            impersonate={impersonate ? user : null}
                        />
                    );
                case "blog_post_single":
                    return (
                        <BlockBlogPostSingle
                            key={block.id}
                            user={user}
                            block={block}
                            update={BlockUpdate}
                            admin={admin}
                            style={style}
                            theme={blogTheme}
                            assets={assets}
                            newBlock={newBlock}
                            posts={posts || []}
                            accountTheme={theme}
                        />
                    );
                case "blog_post_latest":
                    return (
                        <BlockBlogPostLatest
                            key={block.id}
                            user={user}
                            block={block}
                            update={BlockUpdate}
                            admin={admin}
                            style={style}
                            theme={theme}
                            assets={assets}
                            newBlock={newBlock}
                            accountTheme={theme}
                        />
                    );
                case "blog_post_all":
                    return (
                        <BlockBlogPostAll
                            key={block.id}
                            user={user}
                            block={block}
                            update={BlockUpdate}
                            admin={admin}
                            style={style}
                            theme={theme}
                            assets={assets}
                            newBlock={newBlock}
                            accountTheme={theme}
                        />
                    );
                case "twitter_feed":
                    return (
                        <BlockTwitterFeed
                            key={block.id}
                            block={block}
                            update={BlockUpdate}
                            admin={admin}
                            newBlock={newBlock}
                        />
                    );
                case "fourthwall_products":
                    return (
                        <BlockFourthWallProducts
                            key={block.id}
                            user={user}
                            block={block}
                            update={BlockUpdate}
                            admin={admin}
                            style={style}
                            theme={theme}
                            assets={assets}
                            newBlock={newBlock}
                        />
                    );
                case "throne_wishlist":
                    return (
                        <BlockThroneWishlist
                            key={block.id}
                            user={user}
                            block={block}
                            update={BlockUpdate}
                            admin={admin}
                            style={style}
                            theme={theme}
                            assets={assets}
                            newBlock={newBlock}
                        />
                    );
                case "form":
                    return (
                        <BlockForm
                            key={block.id}
                            user={user}
                            block={block}
                            update={BlockUpdate}
                            admin={admin}
                            style={style}
                            theme={theme}
                            assets={assets}
                            newBlock={newBlock}
                        />
                    );
                case "kick":
                    return (
                        <BlockKick
                            key={block.id}
                            user={user}
                            block={block}
                            update={BlockUpdate}
                            admin={admin}
                            style={style}
                            theme={theme}
                            newBlock={newBlock}
                        />
                    );
                default:
                    return <div key={block.id}>{block.name}</div>;
            }
        })();
    }
}
